import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../styles/pages/logs.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRight, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { usePartnerRoles } from '@hooks/usePartnerRoles';
import { usePartnerOrganizationsQuery } from '@hooks/requests/queries/use_partner_organizations_query';
import { usePartnerCompaniesAllQuery } from '@hooks/requests/queries/use_partner_companies_all_query';
import { usePartnerLogsQuery } from '@hooks/requests/queries/use_partner_logs_query';
import type { PartnerLogEntry } from '@hooks/requests/queries/use_partner_logs_query';
import {
  LogsFilterBar,
  DEFAULT_LOGS_FILTERS,
  type LogsFilters,
  LogEventDetailsModal,
  LogsFilteredEmptyState,
  formatLogTimestamp,
} from '@components/logging';
import { LastUpdatedStatus } from '@ui';

// "Event type" filter = request method. A fixed list, not fetched: only M2M
// requests are logged, and every M2M-reachable partner route is one of these
// four methods (the backend validates the same set) — a method with no log
// entries yet must still be pickable.
const HTTP_METHOD_OPTIONS = ['GET', 'POST', 'PATCH', 'DELETE'].map((method) => ({
  label: method,
  value: method,
}));

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_LABEL: Record<string, string> = { success: 'Success', error: 'Error' };

/** How long free-text search waits after the last keystroke before firing a request. */
const SEARCH_DEBOUNCE_MS = 300;

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Logs page filter controls + events table (sc-44216 / sc-44238, Figma node
 * 2774:11369): Dates, Status, Details, Organization (AceUp staff only), and
 * Company. "Key Name" from the mockup isn't rendered — the backend log entry
 * has no api_key_id/name (see partner_models.PartnerApiLogEntry's docstring:
 * it was intentionally dropped from an earlier schema draft), so there's
 * nothing to display; this column can be added once that field exists.
 *
 * Scrolling to the bottom of the table drives the backend's cursor
 * pagination via an IntersectionObserver on a sentinel row (sc-44238 AC:
 * "automatically loads remaining events when the user scrolls down...
 * doesn't show pagination") — there's no "Load more" button. The Dates
 * header is rendered as a static "sorted desc" indicator (matching the
 * design) rather than a real sort toggle, since the API only returns one
 * stable newest-first order.
 */
const LogsPage: React.FC = () => {
  const { isAceUpStaff } = usePartnerRoles();

  // A single filter state now — every control in LogsFilterBar auto-applies
  // as soon as it changes (the one exception, the custom date range's
  // From/To, is staged entirely inside LogsFilterBar itself and only
  // reaches `onChange`/here once its own inline Apply is clicked). Free
  // text is the only field that still needs debouncing below, since typing
  // shouldn't fire a request per keystroke.
  const [filters, setFilters] = useState<LogsFilters>(DEFAULT_LOGS_FILTERS);
  const handleClearFilters = () => setFilters(DEFAULT_LOGS_FILTERS);

  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(filters.search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [filters.search]);

  // sc-44238 State 2 — the row the user clicked, or null when the modal is closed.
  const [selectedEntry, setSelectedEntry] = useState<PartnerLogEntry | null>(null);

  // AceUp staff: org list for the Org filter + (when a specific org is
  // picked) to scope the Company filter. Partner roles never see the Org
  // filter, so this stays disabled for them.
  const { data: organizations } = usePartnerOrganizationsQuery(isAceUpStaff);
  // For AceUp staff, the Company filter stays disabled (see LogsFilterBar)
  // until a specific org is picked — an "All orgs" fetch here would
  // otherwise return an unscoped, cross-tenant company list for a control
  // the user can't even open yet.
  const { data: allCompanies } = usePartnerCompaniesAllQuery({
    organizationId: isAceUpStaff ? filters.organizationId : undefined,
  });

  const organizationOptions = useMemo(
    () =>
      (organizations ?? [])
        .map((o) => ({
          label: o.organizationName?.trim() || o.organizationId,
          value: o.organizationId,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [organizations]
  );

  const companyOptions = useMemo(
    () =>
      (allCompanies ?? [])
        .map((c) => ({ label: c.companyName, value: c.companyId }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [allCompanies]
  );

  const isCustomRange = filters.dateRange.preset === 'custom';
  const range = isCustomRange ? undefined : filters.dateRange.preset;
  const from = isCustomRange ? filters.dateRange.from : undefined;
  const to = isCustomRange ? filters.dateRange.to : undefined;

  // Cursor pagination is owned by react-query here (see usePartnerLogsQuery):
  // a filter change below gets its own cache key and so naturally starts
  // over at page one, with no manual reset needed.
  const { data, isLoading, isError, isFetchingNextPage, fetchNextPage, hasNextPage } =
    usePartnerLogsQuery({
      organizationId: isAceUpStaff ? filters.organizationId : undefined,
      companyId: filters.companyId,
      status: filters.status,
      httpMethod: filters.httpMethod,
      range,
      from,
      to,
      search: debouncedSearch || undefined,
      pageSize: 50,
    });

  const entries = useMemo(() => data?.pages.flatMap((page) => page.events) ?? [], [data]);

  // ── Scroll-triggered pagination ────────────────────────────────────────────
  // A sentinel row sits right after the last real row; once it's on screen
  // (or close to it — rootMargin gives it a head start) the next page is
  // requested. No visible "Load more" control.
  const sentinelRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage) return;

    const observer = new IntersectionObserver(
      ([sentinelEntry]) => {
        if (sentinelEntry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const orgLabel = (id: string | null | undefined): string =>
    organizationOptions.find((o) => o.value === id)?.label ?? '—';

  const companyLabel = (id: string | null | undefined): string =>
    companyOptions.find((o) => o.value === id)?.label ?? '—';

  const handleRowOpen = useCallback((entry: PartnerLogEntry) => setSelectedEntry(entry), []);

  // Dates + Status + Details + Company, plus Organization for AceUp staff.
  const columnCount = isAceUpStaff ? 5 : 4;
  const hasNoResults = !isLoading && !isError && entries.length === 0;

  return (
    <div className="logs-page">
      <div className="logs-page-head">
        <h1 className="api-keys-title">Logs</h1>
        <LastUpdatedStatus />
      </div>

      <LogsFilterBar
        filters={filters}
        onChange={setFilters}
        onClear={handleClearFilters}
        isAceUpStaff={isAceUpStaff}
        organizationOptions={organizationOptions}
        companyOptions={companyOptions}
        httpMethodOptions={HTTP_METHOD_OPTIONS}
      />

      <section className="api-keys-card" aria-label="Events">
        <p className="account-eyebrow">Events</p>

        <div className="api-keys-bar">
          <p className="api-keys-bar-count">
            <strong>{entries.length}</strong>
            {entries.length === 1 ? ' event' : ' events'}
          </p>
        </div>

        <div className="api-keys-table-wrap">
          {/* sc-45715 AC5: the Partner Admin/Viewer table has no
              Organization column between Details and Company, so
              Details' auto width consumes the whole remaining row and
              strands Company far right with a big gap before it —
              lp-table--partner caps Details to pull Company back left. */}
          <table className={`api-keys-table lp-table${isAceUpStaff ? '' : ' lp-table--partner'}`}>
            <colgroup>
              <col className="lp-col-dates" />
              <col className="lp-col-status" />
              <col className="lp-col-details" />
              {isAceUpStaff && <col className="lp-col-org" />}
              <col className="lp-col-company" />
            </colgroup>
            <thead>
              <tr>
                <th>
                  {/* Backend only supports one stable newest-first order —
                      this reflects that as a static indicator rather than a
                      clickable toggle (see the component doc comment). */}
                  <span className="lp-th-sorted">
                    Dates
                    <FontAwesomeIcon icon={faChevronDown} className="lp-th-sort-icon" />
                  </span>
                </th>
                <th>Status</th>
                <th>Details</th>
                {isAceUpStaff && <th>Organization</th>}
                <th>Company</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && entries.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} className="lp-empty-cell">
                    Loading…
                  </td>
                </tr>
              ) : isError ? (
                <tr>
                  <td colSpan={columnCount} className="lp-empty-cell">
                    Failed to load logs
                  </td>
                </tr>
              ) : hasNoResults ? (
                // Zero events, whatever the current filters are — there's no
                // separate "nothing in the org yet" page state anymore.
                <tr>
                  <td colSpan={columnCount} className="lp-empty-cell lp-empty-cell--filtered">
                    <LogsFilteredEmptyState onClearFilters={handleClearFilters} />
                  </td>
                </tr>
              ) : (
                <>
                  {entries.map((entry, index) => (
                    <tr
                      key={`${entry.requestId ?? index}-${entry.timestamp}`}
                      className="api-keys-row lp-row"
                      onClick={() => handleRowOpen(entry)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleRowOpen(entry);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <td className="lp-td">{formatLogTimestamp(entry.timestamp)}</td>
                      <td className="lp-td">
                        <span className={`lp-status-badge lp-status-badge--${entry.status}`}>
                          {STATUS_LABEL[entry.status] ?? entry.status}
                        </span>
                      </td>
                      <td className="lp-td lp-td--details">{entry.details}</td>
                      {isAceUpStaff && <td className="lp-td">{orgLabel(entry.organizationId)}</td>}
                      <td className="lp-td lp-td--company">
                        <span className="lp-td-company-inner">
                          {/* sc-45715 AC4: a bare text node inside a flex row
                              never truncates on its own — only this span's
                              own overflow/ellipsis rule can, which is why a
                              long company name used to just push past the
                              table edge instead of getting cut off. */}
                          <span className="lp-td-company-label">
                            {companyLabel(entry.companyId)}
                          </span>
                          <FontAwesomeIcon icon={faArrowUpRight} className="lp-row-arrow" />
                        </span>
                      </td>
                    </tr>
                  ))}
                  {/* Scroll sentinel — never itself visible; once it enters the
                      viewport (or comes within rootMargin of it) the next page
                      is requested automatically. */}
                  {hasNextPage && (
                    <tr ref={sentinelRef} className="lp-sentinel-row" aria-hidden="true">
                      <td colSpan={columnCount} />
                    </tr>
                  )}
                  {/* Confirms a scroll-triggered fetch actually went out and
                      more rows are coming, right where they'll land. Lines up
                      under Details rather than spanning the row. */}
                  {isFetchingNextPage && (
                    <tr className="lp-loading-more-row">
                      <td className="lp-td" />
                      <td className="lp-td" />
                      <td className="lp-td lp-loading-more-cell">Loading...</td>
                      {isAceUpStaff && <td className="lp-td" />}
                      <td className="lp-td" />
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <LogEventDetailsModal
        isOpen={selectedEntry !== null}
        onClose={() => setSelectedEntry(null)}
        entry={selectedEntry}
      />
    </div>
  );
};

export default LogsPage;
